import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type GmailAuditLog = components["schemas"]["GmailAuditLogResponse"];
type GmailAuditLogEventType = components["schemas"]["GmailAuditLogEventType"];

const EVENT_LABELS: Record<GmailAuditLogEventType, string> = {
  ConnectionCreated: "Connection created",
  ConnectionRevoked: "Connection revoked",
  ReauthRequired: "Reauth required",
  CandidateDetected: "Candidate detected",
  CandidateImported: "Candidate imported",
};

function eventBadgeVariant(type: GmailAuditLogEventType): "default" | "secondary" | "destructive" | "outline" {
  switch (type) {
    case "ConnectionCreated":
    case "CandidateImported":
      return "default";
    case "ConnectionRevoked":
    case "ReauthRequired":
      return "destructive";
    case "CandidateDetected":
    default:
      return "secondary";
  }
}

interface GmailAuditLogsTableProps {
  organisationId: string;
  gmailConnectionId?: string;
}

export function GmailAuditLogsTable({ organisationId, gmailConnectionId }: GmailAuditLogsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });
  const [logs, setLogs] = useState<GmailAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchLogs() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(
        "/admin/organisations/{organisationId}/gmail-audit-logs",
        {
          params: {
            path: { organisationId },
            query: {
              Cursor: pagination.cursor,
              Limit: pagination.limit,
              GmailConnectionId: gmailConnectionId,
            },
          },
        },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load Gmail audit logs.");
      } else {
        setLogs(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [organisationId, gmailConnectionId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Connection</TableHead>
              <TableHead>Candidate</TableHead>
              <TableHead>Bill</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Created at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  No Gmail audit log entries for this organisation.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge variant={eventBadgeVariant(log.eventType)}>
                      {EVENT_LABELS[log.eventType]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {log.gmailConnectionId ? (
                      <Link to={`/gmail-connections/${log.gmailConnectionId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {log.gmailConnectionId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.gmailCandidateId ? (
                      <div className="inline-flex items-center gap-1.5">
                        <Badge variant="secondary" className="font-mono text-xs">
                          {log.gmailCandidateId.slice(0, 8)}...
                        </Badge>
                        <CopyButton value={log.gmailCandidateId} />
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.billId ? (
                      <Link to={`/bills/${log.billId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {log.billId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.actorUserId ? (
                      <Link to={`/users/${log.actorUserId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {log.actorUserId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
